/**
 * Navigation – portage de design/components/bundle.js (Breadcrumb, AppHeader, Sidebar, Menu, CommandPalette).
 * Comportement des éléments interactifs délégué à Radix UI (même DOM et mêmes classes `ag-…`) : Menu = Radix
 * DropdownMenu (avec `trigger`) ou groupe à focus itinérant (dans un panneau déjà ouvert), CommandPalette = Radix Dialog.
 */
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as RovingFocus from '@radix-ui/react-roving-focus';
import { cx, h, Icon, ModuleTag, useIdStable, type IconName, type Metier } from './base';
import { useRetourFocus } from './dialogues';
import { EntitySwitcher, type EntitySwitcherProps } from './filiale';

/* ---------- Breadcrumb (fil d'Ariane + retour) ---------- */
export interface BreadcrumbProps { items: { label: string; href?: string; metier?: Metier }[]; backLabel: string; onBack?: () => void; ariaLabel?: string }

export function Breadcrumb(p: BreadcrumbProps) {
  const items = p.items || [];
  return h('div', { className: 'ag-crumbs' },
    h('button', { type: 'button', className: cx('ag-back', 'ag-glass'), 'aria-label': p.backLabel, title: p.backLabel, onClick: p.onBack }, h(Icon, { name: 'back', size: 18 })),
    h('nav', { 'aria-label': p.ariaLabel }, h('ol', { className: 'ag-crumbs-list' }, items.map((it, i) => {
      const dernier = i === items.length - 1;
      return h('li', { key: i },
        it.metier && i === 0 ? h('span', { className: cx('ag-crumb-dot', 'ag-mod-' + it.metier), 'aria-hidden': true }) : null,
        dernier ? h('span', { className: 'ag-crumb-cur', 'aria-current': 'location' }, it.label) : h('a', { href: it.href || '#' }, it.label),
        !dernier ? h(Icon, { name: 'chevron', size: 14, className: 'ag-crumb-sep' }) : null);
    }))));
}

/* ---------- AppHeader ---------- */
export interface AppHeaderProps {
  breadcrumb?: BreadcrumbProps; brand?: string; searchLabel?: string; searchHint?: string; onSearch?: () => void;
  entity?: EntitySwitcherProps; actions?: React.ReactNode;
  /**
   * Ajout AGORA : liste des filiales ouverte par le sélecteur (menu déroulant Radix, choix exclusif : `activeId` =
   * filiale courante). Remplace `entityPopover` (contenu ancré géré par l'appelant), conservé pour compatibilité.
   */
  entityMenu?: Omit<MenuProps, 'trigger'>;
  entityPopover?: React.ReactNode;
  /** Ajout AGORA : indication placée avant le sélecteur de filiale (ex. « Vue consolidée · lecture seule »). */
  entityNote?: React.ReactNode;
}

export function AppHeader(p: AppHeaderProps) {
  const selecteur = p.entity ? h(EntitySwitcher, p.entity) : null;
  return h('header', { className: 'ag-header' },
    h('div', { className: 'ag-header-start' }, p.breadcrumb ? h(Breadcrumb, p.breadcrumb) : null,
      p.brand ? h('span', { className: 'ag-header-brand' }, p.brand) : null, p.actions || null),
    p.searchLabel
      ? h('button', { type: 'button', className: cx('ag-header-search', 'ag-glass'), onClick: p.onSearch },
        h(Icon, { name: 'search', size: 16 }), h('span', null, p.searchLabel), p.searchHint ? h('kbd', null, p.searchHint) : null)
      : h('span'),
    h('div', { className: 'ag-header-end' }, p.entityNote || null,
      selecteur && p.entityMenu ? h(Menu, { side: 'bottom', align: 'end', ...p.entityMenu, trigger: selecteur }) : selecteur,
      p.entityPopover || null));
}

/* ---------- Sidebar (desktop) : Métiers › Modules › Sous-modules ---------- */
export interface SidebarLeaf { id: string; label: string; icon?: IconName; count?: number; href?: string; open?: boolean; children?: SidebarLeaf[] }
export interface SidebarMetier { id: string; label: string; metier: Metier; open?: boolean; children: SidebarLeaf[] }
export interface SidebarProps {
  brand?: React.ReactNode; sections: { title?: string; items: (SidebarLeaf | SidebarMetier)[] }[]; activeId?: string; footer?: React.ReactNode;
  ariaLabel?: string; collapsed?: boolean; onToggle?: () => void; onToggleItem?: (id: string) => void; collapseLabel?: string; expandLabel?: string;
  /** Ajout AGORA : clic sur la pastille d'un métier quand la barre est repliée en rail. */
  onMetier?: (id: string) => void;
}

const estMetier = (it: SidebarLeaf | SidebarMetier): it is SidebarMetier => 'metier' in it;

export function Sidebar(p: SidebarProps) {
  const [ouverts, setOuverts] = React.useState<Record<string, boolean>>(() => {
    const o: Record<string, boolean> = {};
    const parcourir = (liste: SidebarLeaf[]) => liste.forEach((it) => { if (it.children) { o[it.id] = !!it.open; parcourir(it.children); } });
    (p.sections || []).forEach((sec) => parcourir(sec.items as SidebarLeaf[]));
    return o;
  });
  const [replieInterne, setReplieInterne] = React.useState(!!p.collapsed);
  const col = p.onToggle ? !!p.collapsed : replieInterne;
  const estOuvert = (it: SidebarLeaf | SidebarMetier) => (p.onToggleItem ? !!it.open : !!ouverts[it.id]);
  const basculer = (it: SidebarLeaf | SidebarMetier) => {
    if (p.onToggleItem) p.onToggleItem(it.id);
    else setOuverts((o) => ({ ...o, [it.id]: !o[it.id] }));
  };
  const contient = (it: SidebarLeaf | SidebarMetier): boolean => it.id === p.activeId || (it.children || []).some(contient);
  const pli = (open: boolean, cls: string, enfants: React.ReactNode) =>
    h('div', { className: cx('ag-side-fold', open && 'ag-side-fold-open'), 'aria-hidden': !open, inert: open ? undefined : '' },
      h('div', { className: 'ag-side-fold-in' }, h('div', { className: cls }, enfants)));
  const feuille = (it: SidebarLeaf, profondeur = 1): React.ReactNode => {
    if (it.children && it.children.length) {
      const actif = contient(it), op = estOuvert(it);
      return h('div', { key: it.id, className: 'ag-side-group' },
        h('button', { type: 'button', className: cx('ag-side-item', 'ag-side-module', actif && 'ag-side-parent-on'), 'aria-expanded': op, onClick: () => basculer(it) },
          h('span', { className: 'ag-side-label' }, it.label),
          it.count ? h('span', { className: cx('ag-side-count', op && 'ag-side-count-hide') }, it.count) : null,
          h(Icon, { name: 'chevron', size: 14, className: op ? 'ag-rot' : 'ag-rot-closed' })),
        pli(op, cx('ag-side-children', 'ag-side-level' + (profondeur + 1)), it.children.map((c) => feuille(c, profondeur + 1))));
    }
    const on = it.id === p.activeId;
    return h('a', { key: it.id, href: it.href || '#', title: col ? it.label : undefined, className: cx('ag-side-item', on && 'ag-side-on'), 'aria-current': on ? 'page' : undefined },
      it.icon ? h(Icon, { name: it.icon, size: 20 }) : null,
      h('span', { className: 'ag-side-label' }, it.label),
      it.count ? h('span', { className: 'ag-side-count' }, it.count) : null);
  };
  return h('nav', { className: cx('ag-side', 'ag-glass', col && 'ag-side-collapsed'), 'aria-label': p.ariaLabel },
    h('div', { className: 'ag-side-top' },
      p.brand && !col ? h('div', { className: 'ag-side-brand' }, p.brand) : null,
      h('button', {
        type: 'button', className: 'ag-side-toggle', 'aria-expanded': !col, 'aria-label': col ? p.expandLabel : p.collapseLabel,
        title: col ? p.expandLabel : p.collapseLabel, onClick: () => (p.onToggle ? p.onToggle() : setReplieInterne(!col)),
      }, h(Icon, { name: 'sidebar', size: 20 }))),
    (p.sections || []).map((sec, si) =>
      h('div', { key: si, className: 'ag-side-sec' },
        sec.title && !col ? h('div', { className: 'ag-side-title' }, sec.title) : col && si ? h('div', { className: 'ag-side-rule' }) : null,
        sec.items.map((it) => {
          if (!estMetier(it)) return feuille(it);
          const actif = contient(it), op = estOuvert(it);
          return h('div', { key: it.id, className: 'ag-side-group' },
            h('button', {
              type: 'button', title: col ? it.label : undefined, className: cx('ag-side-item', 'ag-side-metier', actif && 'ag-side-metier-on'),
              'aria-expanded': col ? undefined : op, 'aria-haspopup': col ? 'menu' : undefined,
              onClick: () => (col ? p.onMetier?.(it.id) : basculer(it)),
            },
            h('span', { className: cx('ag-side-mod', 'ag-mod-' + it.metier) }),
            h('span', { className: 'ag-side-label' }, it.label),
            h(Icon, { name: 'chevron', size: 16, className: op ? 'ag-rot' : 'ag-rot-closed' })),
            !col ? pli(op, 'ag-side-children', it.children.map((c) => feuille(c, 1))) : null);
        }))),
    p.footer && !col ? h('div', { className: 'ag-side-foot' }, p.footer) : null);
}

/* ---------- Menu (liste déroulante / contextuelle) ---------- */
export type MenuItem = {
  id: string; label: string; icon?: IconName; shortcut?: string; danger?: boolean;
  /** Ajouts AGORA : action indisponible ; visuel en tête à la place de l'icône (drapeau…) ; mention en fin de ligne (code…). */
  disabled?: boolean; leading?: React.ReactNode; trailing?: React.ReactNode;
} | { divider: true };
export interface MenuProps {
  items: MenuItem[]; title?: string; activeId?: string; onSelect?: (id: string) => void; ariaLabel?: string; className?: string;
  /**
   * Ajouts AGORA – menu déroulant (Radix DropdownMenu) : `trigger` = le bouton qui l'ouvre (un élément qui transmet sa
   * référence et ses props à son bouton : Button, EntitySwitcher, LocaleSwitcher…). Ouverture au clic, Entrée, Espace
   * ou ↓ ; ↑ ↓ Début Fin et première lettre pour se déplacer ; Échap ou clic à côté pour fermer, le focus revient au
   * bouton. Position : `side` (bas par défaut), `align` (début par défaut), `sideOffset` (8 px) ; l'état peut être
   * piloté (`open`, `onOpenChange`).
   * Sans `trigger`, le menu est affiché tel quel dans un panneau déjà ouvert (ex. menu Profil du dock) : ↑ ↓ Début Fin
   * entre les actions, un seul arrêt de tabulation.
   * Avec `activeId`, les actions deviennent un choix exclusif (`menuitemradio`, coché = activeId : langue, filiale…).
   */
  trigger?: React.ReactElement; open?: boolean; onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'right' | 'bottom' | 'left'; align?: 'start' | 'center' | 'end'; sideOffset?: number;
}

/** Contenu d'une action (passé en enfants séparés : pas de clé à donner). */
function contenuAction(it: Extract<MenuItem, { id: string }>): React.ReactNode[] {
  return [
    it.leading ?? (it.icon ? h(Icon, { name: it.icon, size: 18 }) : h('span', { style: { width: 18 } })),
    h('span', { className: 'ag-menu-label' }, it.label),
    it.trailing ?? null,
    it.shortcut ? h('kbd', null, it.shortcut) : null,
  ];
}

export function Menu(p: MenuProps) {
  return p.trigger ? h(MenuDeroulant, p) : h(MenuEnPlace, p);
}

/** Menu ouvert par son bouton : Radix DropdownMenu, sans portail (le verre et les styles restent ceux du design). */
function MenuDeroulant(p: MenuProps) {
  const choix = p.activeId !== undefined;
  const actions = (p.items || []).map((it, i) => {
    if ('divider' in it) return h(DropdownMenu.Separator, { key: 'd' + i, className: 'ag-menu-div' });
    const classes = cx('ag-menu-item', it.danger && 'ag-menu-danger', it.id === p.activeId && 'ag-menu-on');
    return choix
      ? h(DropdownMenu.RadioItem, { key: it.id, value: it.id, disabled: it.disabled, className: classes }, ...contenuAction(it))
      : h(DropdownMenu.Item, { key: it.id, disabled: it.disabled, className: classes, onSelect: () => p.onSelect?.(it.id) }, ...contenuAction(it));
  });
  return h(DropdownMenu.Root, { open: p.open, onOpenChange: p.onOpenChange, modal: false },
    h(DropdownMenu.Trigger, { asChild: true }, p.trigger),
    h(DropdownMenu.Content, {
      className: cx('ag-menu', 'ag-glass', 'ag-glass-strong', p.className), 'aria-label': p.ariaLabel,
      side: p.side ?? 'bottom', align: p.align ?? 'start', sideOffset: p.sideOffset ?? 8, collisionPadding: 12, loop: true,
    },
    p.title ? h(DropdownMenu.Label, { className: 'ag-menu-title' }, p.title) : null,
    choix ? h(DropdownMenu.RadioGroup, { value: p.activeId, onValueChange: (v: string) => p.onSelect?.(v) }, actions) : actions));
}

/** Menu affiché dans un panneau déjà ouvert : focus itinérant de Radix (celui des menus, onglets et groupes radio). */
function MenuEnPlace(p: MenuProps) {
  const choix = p.activeId !== undefined;
  return h(RovingFocus.Root, { asChild: true, orientation: 'vertical', loop: true },
    h('div', { className: cx('ag-menu', 'ag-glass', 'ag-glass-strong', p.className), role: 'menu', 'aria-label': p.ariaLabel, 'aria-orientation': 'vertical' },
      p.title ? h('div', { className: 'ag-menu-title' }, p.title) : null,
      (p.items || []).map((it, i) => {
        if ('divider' in it) return h('div', { key: 'd' + i, className: 'ag-menu-div', role: 'separator' });
        const on = it.id === p.activeId;
        return h(RovingFocus.Item, { key: it.id, asChild: true, focusable: !it.disabled, active: on },
          h('button', {
            type: 'button', role: choix ? 'menuitemradio' : 'menuitem', 'aria-checked': choix ? on : undefined, disabled: it.disabled,
            className: cx('ag-menu-item', it.danger && 'ag-menu-danger', on && 'ag-menu-on'),
            onClick: () => p.onSelect?.(it.id),
          }, ...contenuAction(it)));
      })));
}

/* ---------- CommandPalette (recherche Ctrl/⌘ K) ---------- */
export interface CommandPaletteProps {
  placeholder: string; query?: string; hint?: string; activeId?: string; ariaLabel?: string;
  groups: { label: string; items: { id: string; label: string; icon?: IconName; metier?: Metier; moduleLabel?: string; hint?: string }[] }[];
  /** Ajouts AGORA : saisie contrôlée, choix d'un résultat, clavier, message si vide. */
  onQuery?: (q: string) => void; onSelect?: (id: string) => void; onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void; emptyLabel?: string;
  /**
   * Ajout AGORA : fenêtre modale (Radix Dialog) sur un voile `agx-voile` – focus gardé dans la palette, Échap ou clic
   * sur le voile pour fermer (→ onClose), focus rendu à l'élément d'origine, page derrière inerte et sans défilement.
   * Sans onClose : la boîte seule, comme la référence.
   */
  onClose?: () => void;
}

export function CommandPalette(p: CommandPaletteProps) {
  const idListe = useIdStable('ag-cmd');
  const retourFocus = useRetourFocus(!!p.onClose);
  const saisie = p.onQuery
    ? { value: p.query ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => p.onQuery!(e.target.value) }
    : { defaultValue: p.query };
  const groupes = (p.groups || []).filter((g) => g.items.length);
  const vide = !groupes.length;
  const idOption = (id: string) => `${idListe}-${id}`;
  // Résultat actif (flèches) : annoncé par aria-activedescendant et gardé visible dans la liste.
  React.useEffect(() => {
    if (p.activeId) document.getElementById(`${idListe}-${p.activeId}`)?.scrollIntoView({ block: 'nearest' });
  }, [p.activeId, idListe]);
  const boite = (titre: React.ReactNode, props: Record<string, unknown>) => h('div', { className: cx('ag-cmd', 'ag-glass', 'ag-glass-strong'), ...props },
    titre,
    h('div', { className: 'ag-cmd-search' }, h(Icon, { name: 'search', size: 20 }),
      h('input', {
        // En fenêtre modale, Radix place le focus sur la saisie (et retient l'élément d'origine pour le lui rendre).
        ...saisie, placeholder: p.placeholder, 'aria-label': p.placeholder, autoFocus: !p.onClose, onKeyDown: p.onKeyDown, role: 'combobox',
        'aria-expanded': !vide, 'aria-controls': idListe, 'aria-autocomplete': 'list', 'aria-activedescendant': p.activeId && !vide ? idOption(p.activeId) : undefined,
      }),
      p.hint ? h('kbd', null, p.hint) : null),
    vide && p.emptyLabel ? h('div', { className: 'ag-cmd-group' }, h('div', { className: 'ag-menu-title', role: 'status' }, p.emptyLabel)) : null,
    // Une seule liste (contrôlée par la saisie), les groupes en sous-ensembles nommés.
    h('div', { id: idListe, role: 'listbox', 'aria-label': p.ariaLabel }, groupes.map((g, gi) =>
      h('div', { key: gi, className: 'ag-cmd-group', role: 'group', 'aria-label': g.label }, h('div', { className: 'ag-menu-title', 'aria-hidden': true }, g.label),
        g.items.map((it) =>
          h('div', {
            key: it.id, id: idOption(it.id), className: cx('ag-cmd-item', it.id === p.activeId && 'ag-menu-on'), role: 'option', 'aria-selected': it.id === p.activeId,
            onClick: () => p.onSelect?.(it.id), onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
          },
          h(Icon, { name: it.icon || 'search', size: 18 }), h('span', { className: 'ag-menu-label' }, it.label),
          it.metier ? h(ModuleTag, { metier: it.metier, label: it.moduleLabel || '' }) : null,
          it.hint ? h('span', { className: 'ag-cmd-hint' }, it.hint) : null))))));
  if (!p.onClose) return boite(null, { role: 'dialog', 'aria-label': p.ariaLabel });
  return h(Dialog.Root, { open: true, onOpenChange: (o: boolean) => { if (!o) p.onClose?.(); } },
    h(Dialog.Portal, null,
      // Le voile porte la fenêtre (motif « voile défilant » de Radix) : un clic sur le voile est un clic à côté.
      h(Dialog.Overlay, { className: 'agx-voile' },
        h(Dialog.Content, { asChild: true, 'aria-describedby': undefined, ...retourFocus },
          boite(h(Dialog.Title, { className: 'agx-annonce' }, p.ariaLabel), {})))));
}
